import { fmtDate } from "utils";

type AlertType = "cancellation" | "trial-ending" | "info" | "blocked";

interface SubscriptionAlertBannerProps {
  type: AlertType;
  date?: number; // Unix timestamp
  customMessage?: string;
  blockingCode?: string;
}

function getBlockedMessage(blockingCode?: string): string {
  switch (blockingCode) {
    case "banned_from_free":
      return "Your account is blocked. Upgrade your plan or contact support.";
    case "custom_dunning_ended":
      return "Your account is blocked due to unpaid invoices. Contact support.";
    default:
      return "Your account is blocked. Contact support.";
  }
}

export default function SubscriptionAlertBanner({
  type,
  date,
  customMessage,
  blockingCode,
}: SubscriptionAlertBannerProps) {
  // Format the date if provided
  const formattedDate = date ? fmtDate(new Date(date * 1000)) : null;

  // Determine message based on type
  let message = customMessage;
  if (!message) {
    switch (type) {
      case "cancellation":
        message = formattedDate
          ? `Subscription Cancelled: Benefits end on ${formattedDate}`
          : "Subscription Cancelled";
        break;
      case "trial-ending":
        message = formattedDate
          ? `Your trial ends on ${formattedDate}`
          : "Your trial is ending soon";
        break;
      case "blocked":
        message = getBlockedMessage(blockingCode);
        break;
      case "info":
        message = "Subscription information";
        break;
    }
  }

  // Determine styling based on type
  const styles: Record<AlertType, { bg: string; border: string; text: string }> = {
    cancellation: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-800" },
    "trial-ending": { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800" },
    blocked: { bg: "bg-error-50", border: "border-error-100", text: "text-error-800" },
    info: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-800" },
  };

  const { bg, border, text } = styles[type];

  return (
    <div
      className={`flex items-start gap-3 p-4 border rounded-lg ${bg} ${border}`}
    >
      <p className={`text-sm font-medium ${text}`}>{message}</p>
    </div>
  );
}
